import React from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Default hourly grid lines, in minutes from midnight (07:00 - 18:00).
const DEFAULT_POINTS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => h * 60);

function parseTimeToMinutes(t) {
  if (!t) return null;
  const parts = String(t).split(":").map(Number);
  if (Number.isNaN(parts[0])) return null;
  return parts[0] * 60 + (parts[1] || 0);
}

function formatMinutes(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

const styles = {
  gridTable: { width: "100%", borderCollapse: "collapse", fontSize: "12px" },
  gridTh: { background: "#1a237e", color: "#fff", padding: "8px 10px", textAlign: "center", border: "1px solid #c5cae9", minWidth: "110px" },
  gridThTime: { background: "#283593", color: "#fff", padding: "8px 10px", textAlign: "center", border: "1px solid #c5cae9", minWidth: "110px" },
  gridTd: { padding: "6px 8px", border: "1px solid #e8eaf6", verticalAlign: "top", minHeight: "50px", background: "#fff" },
  gridTdTime: { padding: "6px 10px", border: "1px solid #e8eaf6", background: "#f5f5f5", fontWeight: "600", fontSize: "11px", whiteSpace: "nowrap" },
  cell: { background: "#e8f5e9", borderRadius: "4px", padding: "4px 6px", marginBottom: "4px" },
  cellCourse: { fontWeight: "bold", color: "#1b5e20", fontSize: "11px" },
  cellDetail: { color: "#555", fontSize: "10px", marginTop: "2px" },
  emptyState: { textAlign: "center", color: "#999", padding: "40px" }
};

function buildGrid(entries) {
  // Collect every hour boundary that shows up, plus any entry start/end
  // that doesn't land on a whole hour, so every entry's span aligns exactly
  // to one or more grid rows (Excel-style merged cell).
  const points = new Set(DEFAULT_POINTS);
  const parsed = entries.map(entry => {
    const day = entry.dayOfWeek || entry.day;
    const start = parseTimeToMinutes(entry.startTime);
    const end = parseTimeToMinutes(entry.endTime);
    if (start != null) points.add(start);
    if (end != null) points.add(end);
    return { entry, day, start, end };
  });

  const sortedPoints = Array.from(points).sort((a, b) => a - b);
  const rows = [];
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    rows.push({
      start: sortedPoints[i],
      end: sortedPoints[i + 1],
      label: `${formatMinutes(sortedPoints[i])} - ${formatMinutes(sortedPoints[i + 1])}`
    });
  }

  const grid = {};
  const covered = {};
  DAYS.forEach(day => { grid[day] = {}; covered[day] = new Set(); });

  parsed.forEach(({ entry, day, start, end }) => {
    if (!DAYS.includes(day) || start == null || end == null) return;
    const startRowIdx = rows.findIndex(r => r.start === start);
    const endRowIdx = rows.findIndex(r => r.end === end);
    if (startRowIdx === -1 || endRowIdx === -1 || endRowIdx < startRowIdx) return;
    const span = endRowIdx - startRowIdx + 1;
    if (!grid[day][startRowIdx]) grid[day][startRowIdx] = [];
    grid[day][startRowIdx].push({ entry, span });
    for (let i = 1; i < span; i++) covered[day].add(startRowIdx + i);
  });

  return { rows, grid, covered };
}

function TimetableGrid({ classes, entries, emptyMessage }) {
  const { rows, grid, covered } = buildGrid(entries);

  if (entries.length === 0) {
    return <p className={classes.emptyState}>{emptyMessage}</p>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className={classes.gridTable}>
        <thead>
          <tr>
            <th className={classes.gridThTime}>Time</th>
            {DAYS.map(day => <th key={day} className={classes.gridTh}>{day}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={row.label}>
              <td className={classes.gridTdTime}>{row.label}</td>
              {DAYS.map(day => {
                if (covered[day].has(rowIdx)) return null;
                const cellEntries = grid[day][rowIdx] || [];
                if (cellEntries.length === 0) {
                  return <td key={day} className={classes.gridTd} />;
                }
                const rowSpan = Math.max(...cellEntries.map(ce => ce.span));
                return (
                  <td key={day} className={classes.gridTd} rowSpan={rowSpan}>
                    {cellEntries.map(({ entry }, idx) => {
                      const courseName = entry.course?.name || entry.courseName || entry.courseId || "—";
                      const lecturerName = entry.staff
                        ? `${entry.staff.firstName || ""} ${entry.staff.lastName || ""}`.trim()
                        : entry.lecturerName || "—";
                      return (
                        <div key={idx} className={classes.cell}>
                          <div className={classes.cellCourse}>{courseName}</div>
                          <div className={classes.cellDetail}>{lecturerName}</div>
                          <div className={classes.cellDetail}>{entry.venue || "—"} &middot; {entry.type || "—"}</div>
                        </div>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

TimetableGrid.propTypes = {
  classes: PropTypes.object.isRequired,
  entries: PropTypes.array,
  emptyMessage: PropTypes.string
};

TimetableGrid.defaultProps = {
  entries: [],
  emptyMessage: "No timetable entries found."
};

export default withStyles(styles)(TimetableGrid);
